import React from "react";
import { useQuery } from "@tanstack/react-query";
import API from "../api";
import { CURRENT_SEASON } from "../constants/season";

export default function CompareSheet({ username, competition, season = CURRENT_SEASON, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["compare", username, competition, season],
    enabled: !!username,
    queryFn: () =>
      API.get("/predictions/compare", { params: { username, competition, season } }).then((res) => res.data),
  });

  const gap = data ? data.you.points - data.them.points : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-labelledby="compare-title"
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h2 id="compare-title" className="text-[15px] font-semibold text-ink">
            You vs {username}
          </h2>
          <button type="button" onClick={onClose} className="h-9 px-2 text-sm font-semibold text-gray-600">
            Close
          </button>
        </div>

        <div className="p-4 space-y-4">
          {isLoading && <p className="text-sm text-muted">Comparing…</p>}
          {isError && (
            <p className="text-sm text-red-700">{error?.response?.data?.message || "Couldn't load that comparison."}</p>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[data.you, data.them].map((player) => (
                  <div key={player.username} className="rounded-lg bg-gray-50 px-3 py-3">
                    <p className="text-[12px] text-muted truncate">{player.username}</p>
                    <p className="text-[22px] leading-7 font-bold tabular-nums text-ink">{player.points}</p>
                    <p className="text-[12px] text-muted">{player.exact} exact</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted">
                {gap === 0
                  ? "Level on points this season."
                  : gap > 0
                    ? `You lead by ${gap}.`
                    : `${data.them.username} leads by ${Math.abs(gap)}.`}
              </p>
              {data.fixtures?.length ? (
                <ul className="divide-y divide-line text-sm">
                  {data.fixtures.map((f) => (
                    <li key={`${f.match_date}-${f.home_team}`} className="py-2">
                      <p className="font-medium text-ink">
                        {f.home_team} {f.home_score}–{f.away_score} {f.away_team}
                      </p>
                      <p className="text-[12px] text-muted tabular-nums">
                        You {f.you_home}–{f.you_away} · {f.you_points ?? 0} · {data.them.username} {f.them_home}–{f.them_away} · {f.them_points ?? 0}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted">No finished matches you both picked yet.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
